import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
} from '@mui/material';
import { ContentCopy as CopyIcon, Download as DownloadIcon } from '@mui/icons-material';
import { MediaImage } from '../../../types/media.types';
import { Badge } from '../../../components/common';
import { useSnackbar } from 'notistack';

interface ImageModalProps {
  image: MediaImage | null;
  open: boolean;
  onClose: () => void;
}

export const ImageModal: React.FC<ImageModalProps> = ({ image, open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();

  if (!image) return null;

  const isFromFacebook = image.source === 'facebook_api';

  // Fix URL to ensure it has /v1/ prefix
  const imageUrl = image.url.includes('/v1/')
    ? image.url
    : image.url.replace('/media/', '/v1/media/');

  const handleCopyUrl = () => {
    const url = `${window.location.origin}${imageUrl}`;
    navigator.clipboard.writeText(url).then(() => {
      enqueueSnackbar('URL copied to clipboard', { variant: 'success' });
    });
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = image.name || `image_${image.id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <Box
          component="img"
          src={imageUrl}
          alt={image.name}
          sx={{
            width: '100%',
            height: 'auto',
            maxHeight: '500px',
            objectFit: 'contain',
            mb: 3,
            borderRadius: 1,
          }}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"%3E%3Crect fill="%23f3f4f6" width="300" height="200"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="14" fill="%239ca3af" text-anchor="middle" dy=".3em"%3EImage Preview%3C/text%3E%3C/svg%3E';
          }}
        />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {image.name}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                <strong>Size:</strong> {image.dimensions?.width || 0} × {image.dimensions?.height || 0}
              </Typography>
              <Typography variant="body2">
                <strong>File Size:</strong> {formatBytes(image.size || 0)}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {formatDate(image.uploadDate)}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Badge
                  variant={isFromFacebook ? 'primary' : 'success'}
                  size="medium"
                >
                  {isFromFacebook ? 'Facebook Ad' : 'Uploaded'}
                </Badge>
              </Box>
            </Box>
          </Grid>

          {isFromFacebook && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Ad Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Advertiser:</strong> {image.advertiser || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Campaign:</strong> {image.campaignName || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Ad Name:</strong> {image.adName || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Creative ID:</strong> {image.creative_id}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          startIcon={<CopyIcon />}
          onClick={handleCopyUrl}
          sx={{ textTransform: 'none' }}
        >
          Copy URL
        </Button>
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          variant="contained"
          color="primary"
          sx={{ textTransform: 'none' }}
        >
          Download
        </Button>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
